/* eslint-disable @typescript-eslint/no-explicit-any */
import PlanBuilder from "@/Components/Plan/PlanBuilder";
import { Card } from "@/Components/ui/card";
import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/Components/ui/tabs";
import { columnDisplayNames } from "@/Components/Plan/plan_table/columns";
import { /*TaskGroupEdit,*/ Task, WorkPackage, Workstream } from "@/Components/Plan/Plan";
import { AppContext } from "@/App";
import { useLocation } from "react-router-dom";
import Loading from "@/Components/loading";
//import FrmGroupEditTasks from "@/Components/Plan/frmGroupEditTasks";
import Confirmation from "@/hooks/confirmation";
import WorkBreakdownStructure from "@/Components/Plan/wbs/wbs";
import PageLockProvider, { PageName } from "@/Components/PageLockProvider";
import PlanTable from "@/Components/Plan/plan_table/plan_table";
import CSV_Importer from "@/Components/datatable/data_importer/csv_importer";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";
import { useIsMobile } from "@/hooks/use-mobile";

export type PlanTableColumnVisibility = {
  id: boolean
  parentTaskId: boolean
  workstream: boolean
  workPackage: boolean
  name: boolean
  // description: boolean
  duration: boolean
  startDate: boolean
  finishDate: boolean
  ownerUsername: boolean
  rating: boolean
  completed: boolean
  completedDate: boolean
  isDeliverable: boolean
  isMilestone: boolean
  displayOnCharter: boolean
  displayOnStatusReport: boolean
  hasDependencies: boolean
}

function Plan() {
  const app = React.useContext(AppContext);
  const zoomLevel = useResponsiveZoom();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("treeview");
  const [isLoading, setIsLoading] = useState(true);
  const [workstreams, setWorkstreams] = useState<Workstream[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const locationState = useLocation().state as { passedTaskId?: number, filterCombo?: number, workstreamId?: number } | null;
  const [passedTaskId, setPassedTaskId] = useState<number | null>(locationState?.passedTaskId ?? null);
  const [filterCombo, setFilterCombo] = useState<number | null>(locationState?.filterCombo ?? null);
  const [passedWorkstreamId, setPassedWorkstreamId] = useState<number | null>(locationState?.workstreamId ?? null);
  const [showEditFormInitial, setShowEditFormInitial] = useState(false);

  const [dialogTitle, setDialogTitle] = useState("Group Edit Tasks");
  const [dialogContent, setDialogContent] = useState<JSX.Element | null>(null);

  const [projectStartDate, setProjectStartDate] = useState<Date>(new Date());
  const [projectLifecycleStage, setProjectLifecycleStage] = useState<string>("Initiation");

  const [columnVisibility, setColumnVisibility] = useState<PlanTableColumnVisibility>({
    id: false,
    parentTaskId: false,
    workstream: true,
    workPackage: true,
    name: true,
    // description: true,
    duration: false,
    startDate: true,
    finishDate: true,
    ownerUsername: true,
    rating: true,
    completed: false,
    completedDate: false,
    isDeliverable: false,
    isMilestone: false,
    displayOnCharter: false,
    displayOnStatusReport: false,
    hasDependencies: false,
  });

  const [importedRows, setImportedRows] = useState<any[]>([]);

  React.useEffect(() => {
    if (importedRows.length > 0) {
      buildWorkPackagesFromImportedRows(importedRows);
      setImportedRows([]);
    }
  }, [importedRows]);

  const importDataType: { name: string, type: any, example: string }[] = [
    { name: "workPackage", "type": String, example: "WP-1" },
    { name: "name", "type": String, example: "Task 1" },
    { name: "ownerUsername", "type": String, example: "user@example.com" },
    { name: "startDate", "type": Date, example: "2026-01-13" },
    { name: "finishDate", "type": Date, example: "2026-01-20" },
    { name: "duration", "type": Number, example: "5" },
    { name: "isMilestone", "type": Boolean, example: "true" },
    { name: "isDeliverable", "type": Boolean, example: "false" },
    { name: "displayOnCharter", "type": Boolean, example: "true" },
    { name: "displayOnStatusReport", "type": Boolean, example: "false" },
  ];

  React.useEffect(() => {
    loadColumnVisibility();
  }, [tab])

  React.useEffect(() => {
    refreshData();
  }, [app.selectedProject]);

  function refreshData() {
    if (app.selectedProject !== undefined) {
      setIsLoading(true);
      // fetch workstreams
      fetchWorkstreams().finally(() => {
        setIsLoading(false);
      });
    }
  }

  async function fetchWorkstreams() {
    if (app.selectedProject !== undefined) {
      setIsLoading(true);
      // fetch workstreams
      await fetch("Plan/Plan/" + app.selectedProject.id)
        .then((response) => response.json())
        .then((data) => {
          setWorkstreams(data.workstreams);
          setProjectStartDate(new Date(data.startDate));
          setProjectLifecycleStage(data.lifecycleStage);
        }
        )
        ;
    }
  }

  function buildWorkPackagesFromImportedRows(rows: any[]) {
    const users = app.teamMembers.concat(app.projectManagers).concat(app.sponsors);
    const newWorkPackages: WorkPackage[] = [];
    const newTasksOnly: Task[] = [];

    // get unique workpackage names from the imported rows
    const workPackageNames = Array.from(new Set(rows.map(row => row.workPackage).filter((name) => name && name.trim() !== "")));
    const workstream = workstreams.find(ws => ws.id === app.workPackageOptions?.[0]?.workstreamId);

    if (!workstream) {
      app.showActionNotification("Error", "No workstream available to add tasks to. Please create a workstream first.", "destructive");
      return;
    }
    // create new workpackages if they don't already exist
    workPackageNames.forEach((wpName) => {
      const ExistingWP = workstream.workPackages.find((wp) => wp.name === wpName);

      // create tasks for this workpackage
      const tasksForWP: Task[] = rows.filter(row => row.workPackage && row.workPackage === wpName).map((row, index) => {
        return {
          id: 0,
          name: row.name,
          duration: row.duration,
          startDate: row.startDate,
          finishDate: row.finishDate,
          ownerId: String(users.find(user => user.email.toLowerCase().trim() === row.ownerUsername?.toLowerCase().trim())?.id ?? ""),
          index: index + 1,
          isMilestone: row.isMilestone || false,
          isDeliverable: row.isDeliverable || false,
          displayOnCharter: row.displayOnCharter || false,
          displayOnStatusReport: row.displayOnStatusReport || false,
          status: 4 as 1 | 2 | 4,
          completed: false,
          completedDate: null,
          dependencies: [],
          parentTaskId: undefined,
          subTasks: [],
          hasDependents: false,
          level: 0,
          workPackageId: ExistingWP?.id ?? 0,
          // set other default values as needed
        };
      });

      if (!ExistingWP) {
        // if not, create a new workpackage in the first existing workstream
        const newWorkPackage: WorkPackage = {
          id: 0,
          name: wpName,
          tasks: tasksForWP,
          index: workstream.workPackages.length + 1,
          startDate: tasksForWP.reduce((min, task) => task.startDate < min ? task.startDate : min, tasksForWP[0]?.startDate || new Date()),
          finishDate: tasksForWP.reduce((max, task) => task.finishDate > max ? task.finishDate : max, tasksForWP[0]?.finishDate || new Date()),
          ownerId: "",
        };
        newWorkPackages.push(newWorkPackage);
      } else {
        newTasksOnly.push(...tasksForWP);
      }
    });

    if (newWorkPackages.length > 0) {
      AddNewWorkPackagesFromImport(newWorkPackages);
    }
    if (newTasksOnly.length > 0) {
      AddNewTasksFromImport(newTasksOnly);
    }
  }

  function AddNewWorkPackagesFromImport(newWorkPackages: WorkPackage[]) {
    const workstreamId = app.workPackageOptions?.[0]?.workstreamId;
    if (!workstreamId) {
      app.showActionNotification("Error", "Invalid Workstream ID", "destructive");
      return;
    }

    document.body.style.cursor = 'wait';
    fetch("Plan/WorkPackages/Import/" + workstreamId, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(newWorkPackages)
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to add work packages");
        }
        return response.json();
      })
      .then((newWorkPackages : WorkPackage[]) => {
        const workstreamId = workstreams[0].id; // only one worksteam at the moment
        setWorkstreams(workstreams.map((ws) => {
            if (ws.id === workstreamId) {
                return {
                    ...ws,
                    workPackages: [...ws.workPackages, ...newWorkPackages],
                };
            }
            return ws;
        }));
        app.showActionNotification("Success", "Work packages imported successfully", "success");
      })
      .catch((e) => {
        app.showActionNotification("Error", e.message, "destructive");
      })
      .finally(() => {
        document.body.style.cursor = 'default';
      });
  }

  function AddNewTasksFromImport(newTasks: Task[]) {
    document.body.style.cursor = 'wait';
    fetch("Plan/Tasks/Import", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(newTasks)
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to add tasks");
        }
        return response.json();
      })
      .then((data) => {
        const returnedTasks = data.map((task: Task) => ({
          id: task.id,
          name: task.name,
          index: task.index,
          workPackageId: task.workPackageId,
          duration: task.duration,
          startDate: task.startDate,
          finishDate: task.finishDate,
          ownerId: task.ownerId,
          completed: task.completed,
          isDeliverable: task.isDeliverable,
          isMilestone: task.isMilestone,
          displayOnCharter: task.displayOnCharter,
          displayOnStatusReport: task.displayOnStatusReport,
          dependencies: task.dependencies,
          parentTaskId: task.parentTaskId,
          status: task.status
        }));

        setWorkstreams((prevWorkstreams) =>
            prevWorkstreams.map((ws) => ({
                ...ws,
                workPackages: ws.workPackages.map((wp) => {
                    if (returnedTasks.some((task: Task) => task.workPackageId === wp.id)) {
                        const updatedTasks = [...wp.tasks, ...returnedTasks.filter((task: Task) => task.workPackageId === wp.id)];
                        return {
                            ...wp,
                            tasks: updatedTasks,
                            startDate: new Date(Math.min(...updatedTasks.map(t => new Date(t.startDate).getTime()))),
                            finishDate: new Date(Math.max(...updatedTasks.map(t => new Date(t.finishDate).getTime()))),
                        };
                    }
                    return wp;
                }),
            }))
        );

        app.showActionNotification("Success", "Tasks imported successfully", "success");
      })
      .catch((e) => {
        app.showActionNotification("Error", e.message, "destructive");
      })
      .finally(() => {
        document.body.style.cursor = 'default';
      });
  }

  function loadColumnVisibility() {
    const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_Tasks_columnVisibility");
    if (localStorageValue) {
      const visibiltyValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
      setColumnVisibility({
        id: visibiltyValues.find(val => val.columnId == "id")?.isVisible ?? false,
        parentTaskId: visibiltyValues.find(val => val.columnId == "parentTaskId")?.isVisible ?? false,
        workstream: visibiltyValues.find(val => val.columnId == "workstream")?.isVisible ?? true,
        workPackage: visibiltyValues.find(val => val.columnId == "workPackage")?.isVisible ?? true,
        name: visibiltyValues.find(val => val.columnId == "name")?.isVisible ?? true,
        // description: visibiltyValues.find(val => val.columnId == "description")?.isVisible ?? true,
        duration: visibiltyValues.find(val => val.columnId == "duration")?.isVisible ?? false,
        startDate: visibiltyValues.find(val => val.columnId == "startDate")?.isVisible ?? true,
        finishDate: visibiltyValues.find(val => val.columnId == "finishDate")?.isVisible ?? true,
        ownerUsername: visibiltyValues.find(val => val.columnId == "ownerUsername")?.isVisible ?? true,
        rating: visibiltyValues.find(val => val.columnId == "rating")?.isVisible ?? true,
        completed: visibiltyValues.find(val => val.columnId == "completed")?.isVisible ?? false,
        completedDate: visibiltyValues.find(val => val.columnId == "completedDate")?.isVisible ?? false,
        isDeliverable: visibiltyValues.find(val => val.columnId == "isDeliverable")?.isVisible ?? false,
        isMilestone: visibiltyValues.find(val => val.columnId == "isMilestone")?.isVisible ?? false,
        displayOnCharter: visibiltyValues.find(val => val.columnId == "displayOnCharter")?.isVisible ?? false,
        displayOnStatusReport: visibiltyValues.find(val => val.columnId == "displayOnStatusReport")?.isVisible ?? false,
        hasDependencies: visibiltyValues.find(val => val.columnId == "hasDependencies")?.isVisible ?? false,
      })
    }
  }

  function resetPassedValues() {
    setPassedTaskId(null);
    setFilterCombo(null);
    setPassedWorkstreamId(null);
    setShowEditFormInitial(false);
  }

  const onTabChange = (value: string) => {
    setTab(value);
  };

  if (isLoading) {
    return <Loading />;
  }

  function confirmDeleteTasks(taskIds: number[]) {
    setDialogTitle(taskIds.length > 1 ? "Are you sure you want to delete these tasks?" : "Are you sure you want to delete this task?");
    setDialogContent(<Confirmation onConfirm={() => { groupDeleteTasks(taskIds); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
    setDialogOpen(true);
  }

  function groupDeleteTasks(taskIds: number[]) {
    // delete tasks
    document.body.style.cursor = 'wait';
    fetch("Plan/Tasks", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(taskIds),
    })
      .then((response) => {
        if (response.ok) {
          app.showActionNotification("Success", "Tasks Deleted Successfully!", "success");

          // update local state
          setWorkstreams((prevWorkstreams) => {
            const newWorkstreams = prevWorkstreams.map((workstream) => {
              return {
                ...workstream,
                workPackages: workstream.workPackages.map((workpackage) => {
                  return {
                    ...workpackage,
                    tasks: workpackage.tasks.filter((task) => !taskIds.includes(task.id)),
                  };
                })
                  .filter((workpackage) => workpackage.tasks.length > 0),
              };
            })
              .filter((workstream) => workstream.workPackages.length > 0);
            rebuildWorkPackageOptions(newWorkstreams);
            return newWorkstreams;
          });
        } else {
          if (response.status === 400) {
            response.json().then((error) => {
              app.showActionNotification("Action Disallowed", error.error || "Bad Request", "default");
            });
          } else {
            app.showActionNotification("Error", response.statusText, "destructive");
          }
        }
      }
      )
      .catch((error) => {
        app.showActionNotification("Error", error.message, "destructive");
      }
      ).finally(() => {
        document.body.style.cursor = 'default';
      });
  }

  function editTask(taskId: number) {
    setPassedTaskId(taskId);
    setShowEditFormInitial(true);
    setTab("treeview");
  }

  function groupCompleteTasks(taskIds: number[]) {
    // complete tasks
    document.body.style.cursor = 'wait';
    fetch("Plan/Tasks/Complete", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(taskIds),
    })
      .then((response) => {
        if (response.ok) {
          app.showActionNotification("Success", "Tasks Completed Successfully!", "success");

          // update local state
          setWorkstreams((prevWorkstreams) => {
            return prevWorkstreams.map((workstream) => {
              return {
                ...workstream,
                workPackages: workstream.workPackages.map((workpackage) => {
                  return {
                    ...workpackage,
                    tasks: workpackage.tasks.map((task) => {
                      if (taskIds.includes(task.id)) {
                        return {
                          ...task,
                          completed: true,
                          status: 4,
                        };
                      }
                      return task;
                    }),
                  };
                }),
              };
            });
          });
        } else {
          if (response.status === 400) {
            response.json().then((error) => {
              app.showActionNotification("Action Disallowed", error.error || "Bad Request", "default");
            });
          } else {
            app.showActionNotification("Error", response.statusText, "destructive");
          }
        }
      }
      )
      .catch((error) => {
        app.showActionNotification("Error", error.message, "destructive");
      }
      ).finally(() => {
        document.body.style.cursor = 'default';
      });
  }

  function rebuildWorkPackageOptions(workstreams: Workstream[]) {
    // Rebuilds app.workPackageOptions from the current workstreams state
    const newOptions = workstreams.map((ws) => ({
      workstreamId: ws.id,
      workstreamName: ws.name,
      workPackageNames: ws.workPackages.map((wp) => ({
        workPackageId: wp.id,
        workPackageName: wp.name
      }))
    }));
    app.setWorkPackageOptions(newOptions);
  }

  // function showGroupEditDialog(selectedRows: any[]) {
  //   setDialogTitle("Select Attributes to Update");
  //   setDialogContent(
  //     <div className="flex flex-col min-w-[400px]">
  //       <FrmGroupEditTasks ids={selectedRows.map((row) => row.id)} setDialogOpen={setDialogOpen} groupEditTasks={groupEditTasks} />
  //     </div>
  //   )
  //   setDialogOpen(true);
  // }

  // function groupEditTasks(taskGroupEdit: TaskGroupEdit) {
  //   fetch("Plan/Tasks/TaskGroup", {
  //     method: "PUT",
  //     headers: {
  //       "Content-Type": "application/json",
  //     },
  //     body: JSON.stringify(taskGroupEdit),
  //   })
  //     .then((response) => {
  //       if (response.ok) {
  //         if (taskGroupEdit.enableWorkPackage && taskGroupEdit.workPackage !== 0) {
  //           // create a list of task objects from the task IDs
  //           const tasksToUpdate = taskGroupEdit.taskIDs.map((taskId) => {
  //             const workstream = workstreams.find((ws) => ws.workPackages.some((wp) => wp.tasks.some((task) => task.id === taskId)));
  //             if (workstream) {
  //               const workpackage = workstream.workPackages.find((wp) => wp.tasks.some((task) => task.id === taskId));
  //               if (workpackage) {
  //                 const task = workpackage.tasks.find((task) => task.id === taskId);
  //                 if (task) {
  //                   return {
  //                     ...task,
  //                     ownerId: taskGroupEdit.enableOwnerId ? taskGroupEdit.ownerId : task.ownerId,
  //                     isDeliverable: taskGroupEdit.enableIsDeliverable ? taskGroupEdit.isDeliverable : task.isDeliverable,
  //                     isMilestone: taskGroupEdit.enableIsMilestone ? taskGroupEdit.isMilestone : task.isMilestone,
  //                     displayOnCharter: taskGroupEdit.enableDisplayOnCharter ? taskGroupEdit.displayOnCharter : task.displayOnCharter,
  //                     displayOnStatusReport: taskGroupEdit.enableDisplayOnStatusReport ? taskGroupEdit.displayOnStatusReport : task.displayOnStatusReport,
  //                   };
  //                 }
  //               }
  //             }
  //             return null;
  //           }
  //           ).filter((task) => task !== null);

  //           // remove tasks from their current workpackage
  //           setWorkstreams((prevWorkstreams) =>
  //             prevWorkstreams.map((workstream) => ({
  //               ...workstream,
  //               workPackages: workstream.workPackages.map((workpackage) => ({
  //                 ...workpackage,
  //                 tasks: workpackage.tasks.filter((task) => !taskGroupEdit.taskIDs.includes(task.id)),
  //               })),
  //             }))
  //           );
  //           // add tasks to the new workpackage
  //           setWorkstreams((prevWorkstreams) => {
  //             const newWorkstreams = prevWorkstreams.map((workstream) => ({
  //               ...workstream,
  //               workPackages: workstream.workPackages.map((workpackage) => {
  //                 if (workpackage.id === taskGroupEdit.workPackage) {
  //                   return {
  //                     ...workpackage,
  //                     tasks: [...workpackage.tasks, ...tasksToUpdate],
  //                   };
  //                 }
  //                 return workpackage;
  //               }).filter((workpackage) => workpackage.tasks.length > 0),
  //             })).filter((workstream) => workstream.workPackages.length > 0)
  //             rebuildWorkPackageOptions(newWorkstreams);
  //             return newWorkstreams;
  //           });
  //         }

  //         setWorkstreams((prevWorkstreams) =>
  //           prevWorkstreams.map((workstream) => ({
  //             ...workstream,
  //             workPackages: workstream.workPackages.map((workpackage) => ({
  //               ...workpackage,
  //               tasks: workpackage.tasks.map((task) => {
  //                 if (taskGroupEdit.taskIDs.includes(task.id)) {
  //                   return {
  //                     ...task,
  //                     ownerId: taskGroupEdit.enableOwnerId ? taskGroupEdit.ownerId : task.ownerId,
  //                     isDeliverable: taskGroupEdit.enableIsDeliverable ? taskGroupEdit.isDeliverable : task.isDeliverable,
  //                     isMilestone: taskGroupEdit.enableIsMilestone ? taskGroupEdit.isMilestone : task.isMilestone,
  //                     displayOnCharter: taskGroupEdit.enableDisplayOnCharter ? taskGroupEdit.displayOnCharter : task.displayOnCharter,
  //                     displayOnStatusReport: taskGroupEdit.enableDisplayOnStatusReport ? taskGroupEdit.displayOnStatusReport : task.displayOnStatusReport,
  //                   };
  //                 }
  //                 return task;
  //               }),
  //             })),
  //           }))
  //         );

  //         app.setTaskbarMessage(`${taskGroupEdit.taskIDs.length} Tasks updated`);
  //       } else {
  //         if (response.status === 400) {
  //           response.json().then((error) => {
  //             app.setTaskbarMessage(error.error || "Bad Request");
  //           });
  //         } else {
  //           app.setTaskbarMessage(response.statusText);
  //         }
  //       }
  //     })
  //     .catch((error) => {
  //       app.setTaskbarMessage(error.message);
  //     });
  // }

  const groupActions = [
    {
      name: "Group Edit",
      action: (selectedRows: any[]) => {
        // issues with group edit need fixing so disable for now
        //showGroupEditDialog(selectedRows);
        console.log(selectedRows);
        app.showActionNotification("Action Unavailable", "Group Edit is not available at the moment.", "default");
      },
    },
    {
      name: "Delete",
      action: (selectedRows: any[]) => {
        // confirmDeleteTasks(selectedRows.map((row) => row.id));
        console.log(selectedRows);
        app.showActionNotification("Action Unavailable", "Group Delete is not available at the moment.", "default");
      },
    },
    {
      name: "Complete Tasks",
      action: (selectedRows: any[]) => {
        // groupCompleteTasks(selectedRows.map((row) => row.id));
        console.log(selectedRows);
        app.showActionNotification("Action Unavailable", "Group Complete is not available at the moment.", "default");
      },
    }
  ];

  const tasks = getTableData();

  function getTableData() {
    // Helper to pull possible child collections (handles different key spellings / legacy shapes)
    function getChildren(task: any): any[] {
      if (!task || typeof task !== "object") return [];
      return (task.subTasks || []);
    }

    function collect(task: any, workstream: any, workpackage: any | null) {
      const row = {
        id: task.id,
        parentTaskId: task.parentTaskId ?? undefined,
        name: task.name,
        duration: task.duration,
        dependencies: task.dependencies,
        hasDependencies: Array.isArray(task.dependencies) && task.dependencies.length > 0,
        workstream: workstream.name,
        workPackage: workpackage.name,
        workPackageId: workpackage.id,
        task: task.name,
        startDate: task.startDate,
        finishDate: task.finishDate,
        completed: task.completed,
        completedDate: task.completedDate,
        ownerId: task.ownerId,
        ownerUsername: getUsernameFromId(task.ownerId),
        rating: getRiskFromStatus(task.status),
        isDeliverable: task.isDeliverable,
        isMilestone: task.isMilestone,
        displayOnCharter: task.displayOnCharter,
        displayOnStatusReport: task.displayOnStatusReport,
        status: task.status,
        index: task.index,
      };

      const rows = [row];

      const children = getChildren(task);
      if (Array.isArray(children) && children.length) {
        for (const child of children) {
          rows.push(...collect(child, workstream, workpackage));
        }
      }
      return rows;
    }

    const all: any[] = [];
    for (const ws of workstreams) {
      for (const wp of ws.workPackages) {
        for (const t of wp.tasks) {
          all.push(...collect(t, ws, wp));
        }
      }
    }

    // Optional: verify in console (remove in production)
    // console.debug("Flattened task count:", all.length, all);

    return all;
  }

  function getRiskFromStatus(status: 1 | 2 | 4) {
    switch (status) {
      case 1:
        return "High";
      case 2:
        return "Medium";
      case 4:
        return "Low";
      default:
        return "Low";
    }
  }

  function getUsernameFromId(userId: string) {
    const allUsers = app.projectManagers.concat(app.teamMembers);
    const user = allUsers.find((user) => user.id === userId);
    if (user) {
      return user.email;
    }
    return "";
  }

  if (isMobile) {
    return (
      <Card className="p-2 m-2 flex flex-col h-min" style={{ zoom: zoomLevel }}>
        <div className="text-3xl">Mobile Plan functionality coming soon</div>
        <PlanTable
              tasks={tasks}
              isLoading={isLoading}
              viewOnly={app.authLevel < 4} // viewer can see but not edit
              columnVisibility={columnVisibility}
              columnDisplayNames={columnDisplayNames}
              confirmDeleteTasks={confirmDeleteTasks}
              editTask={editTask}
              groupCompleteTasks={groupCompleteTasks}
              groupActions={groupActions}
              dialogOpen={dialogOpen}
              setDialogOpen={setDialogOpen}
              dialogTitle={dialogTitle}
              dialogContent={dialogContent}
            />
      </Card>
    );
  }

  return (
    <Card className="p-2 m-2 flex flex-col h-min" style={{ zoom: zoomLevel }}>
      <PageLockProvider pageName={PageName.Plan} userId={app.applicationUser?.id as string} projectId={app.selectedProject?.id as number} onRefreshData={refreshData}>
        <Tabs value={tab} onValueChange={onTabChange} defaultValue="treeview" className="w-full p-2">
          <TabsList>
            <TabsTrigger value="treeview"><span className="px-2">Tree View</span></TabsTrigger>
            <TabsTrigger value="tableview"><span className="px-2">Table View</span></TabsTrigger>
            {/* 
          <TabsTrigger value="wbs"><span className="px-2">WBS</span></TabsTrigger>*/}
          </TabsList>
          <TabsContent value="treeview">
            <PlanBuilder
              startDate={projectStartDate}
              projectLifecycleStage={projectLifecycleStage}
              workstreams={workstreams}
              setWorkstreams={setWorkstreams}
              passedTaskId={passedTaskId ? passedTaskId : undefined}
              filterCombo={filterCombo ? filterCombo : undefined}
              passedWorkstreamId={passedWorkstreamId ? passedWorkstreamId : undefined}
              showEditFormInitial={showEditFormInitial}
              resetPassedValues={resetPassedValues}
            />
          </TabsContent>
          <TabsContent value="tableview">
            <div className="flex justify-end"><CSV_Importer propertyTypes={importDataType} setNewRows={setImportedRows} title="Import Plan from CSV" /></div>
            <PlanTable
              tasks={tasks}
              isLoading={isLoading}
              viewOnly={app.authLevel < 4} // viewer can see but not edit
              columnVisibility={columnVisibility}
              columnDisplayNames={columnDisplayNames}
              confirmDeleteTasks={confirmDeleteTasks}
              editTask={editTask}
              groupCompleteTasks={groupCompleteTasks}
              groupActions={groupActions}
              dialogOpen={dialogOpen}
              setDialogOpen={setDialogOpen}
              dialogTitle={dialogTitle}
              dialogContent={dialogContent}
            />
          </TabsContent>
          <TabsContent value="wbs">
            <WorkBreakdownStructure workstreams={workstreams} />
          </TabsContent>
        </Tabs>
      </PageLockProvider>
    </Card>
  );
}

export default Plan;